/**
 * DistributionGaps.tsx — Gap analysis and revenue-opportunity reports.
 *
 * Rebuilt in final pass.
 *
 * Preserves all behavior:
 *   - Filter panel: client, city, outlet type, category, sub-category, currency,
 *     market share %, optional advanced avg revenue override
 *   - Generate report → POST /api/gaps/generate (summary + first page of outlets)
 *   - Summary KPIs: total gaps / total opportunity / avg per outlet / market share
 *   - Paginated outlet table with drilldown detail panel (calc breakdown)
 *   - Export report → XLSX blob download
 *   - Saved reports list: view / export / delete
 *   - viewAsClient locks the client selector to the admin-previewed client
 *
 * Notes on the hardcoded country:
 *   - `country` is locked to 'Serbia' — the backend expects this literal so data
 *     returns correctly. We never surface the string in the UI; normalized cities
 *     come back from /api/gaps/cities keyed by country.
 *   - Currency options keep EUR/USD/GBP/RSD to match backend allowed values.
 *
 * Changes:
 *   - Uses shared UI throughout (Card, Button, Tag, Input, Select, FormField,
 *     StatCard, Icon, Table, toast)
 *   - Saved-reports collapsible + confirmation-free delete flow kept simple
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  Card,
  Button, IconButton,
  Tag, type TagKind,
  Input, Select,
  StatCard, StatGrid,
  Icon,
  Table, type Column,
  EmptyState,
  Pagination,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface ReportOutlet {
  census_id: string;
  outlet_name: string;
  outlet_type: string;
  city: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  estimated_annual_value: number;
  confidence: string;
  location_multiplier?: number;
  baseline_used?: number;
}

interface ReportSummary {
  report_id: string;
  total_gap_outlets: number;
  total_opportunity_value: number;
  currency: string;
  outlets: ReportOutlet[];
}

interface SavedReport {
  id: string;
  client_name: string;
  country: string;
  city: string | null;
  outlet_type: string | null;
  category: string;
  sub_category: string | null;
  currency: string;
  total_gap_outlets: number;
  total_opportunity_value: number;
  market_share_pct: number;
  created_at: string | null;
}

interface ReportDetail {
  report: SavedReport;
  outlets: ReportOutlet[];
  total: number;
  page: number;
  pages: number;
}

/* ═══════════════ HELPERS ═══════════════ */

function fmtCurrency(value: number, currency: string): string {
  const sym = currency === 'EUR' ? '\u20AC' : currency === 'USD' ? '$' : currency === 'GBP' ? '\u00A3' : currency === 'RSD' ? 'RSD ' : '';
  if (value >= 1_000_000) return `${sym}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `${sym}${(value / 1_000).toFixed(0)}K`;
  if (value >= 1_000) return `${sym}${(value / 1_000).toFixed(1)}K`;
  return `${sym}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtFull(value: number, currency: string): string {
  const sym = currency === 'EUR' ? '\u20AC' : currency === 'USD' ? '$' : currency === 'GBP' ? '\u00A3' : currency === 'RSD' ? 'RSD ' : '';
  return `${sym}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function confTagKind(conf: string): TagKind {
  if (conf === 'High') return 'success';
  if (conf === 'Medium') return 'warning';
  return 'danger';
}

const OUTLET_TYPES = ['Shop', 'Supermarket', 'Kiosk', 'Restaurant', 'Petrol Station', 'Bakery', 'Pharmacy'];

/* ═══════════════ PAGE ═══════════════ */

export default function DistributionGaps() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { maskClientName, viewAsClient, modeVersion } = useAdmin();

  // Client list
  const [clients, setClients] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState('');

  // Filters
  // NOTE: country is locked — backend requires this literal for correct data.
  //       We never render the string in the UI.
  const [country] = useState('Serbia');
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [categories, setCategories] = useState<Record<string, string[]>>({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [marketShare, setMarketShare] = useState('20');
  const [clientAvgRevenue, setClientAvgRevenue] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Report state
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [reportPage, setReportPage] = useState(1);
  const [reportDetail, setReportDetail] = useState<ReportDetail | null>(null);
  const [generating, setGenerating] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Drilldown
  const [selectedOutlet, setSelectedOutlet] = useState<ReportOutlet | null>(null);

  // Saved reports
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    document.title = 'Distribution Gaps | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load clients
  useEffect(() => {
    api.get<string[]>('/api/clients').then(r => {
      setClients(r.data);
      if (viewAsClient && r.data.includes(viewAsClient)) {
        setSelectedClient(viewAsClient);
      }
    }).catch(() => {});
  }, [modeVersion, viewAsClient]);

  useEffect(() => {
    if (viewAsClient && clients.includes(viewAsClient)) {
      setSelectedClient(viewAsClient);
    }
  }, [viewAsClient, clients]);

  // Load categories
  useEffect(() => {
    api.get<{ categories: Record<string, string[]> }>('/api/gaps/categories')
      .then(r => setCategories(r.data.categories))
      .catch(() => {});
  }, []);

  // Load cities
  useEffect(() => {
    api.get<{ cities: string[] }>('/api/gaps/cities', { params: { country } })
      .then(r => setCities(r.data.cities))
      .catch(() => {});
  }, [country]);

  // Load saved reports
  const loadSavedReports = useCallback(() => {
    api.get<{ reports: SavedReport[] }>('/api/gaps/reports')
      .then(r => setSavedReports(r.data.reports))
      .catch(() => {});
  }, []);
  useEffect(() => { loadSavedReports(); }, [loadSavedReports, modeVersion]);

  /* Load a specific report when navigated here with ?report=id (from Reports page). */
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (!reportId) return;
    setLoadingReport(true);
    api.get<ReportDetail>(`/api/gaps/reports/${reportId}`, { params: { page: 1, limit: 25 } })
      .then(r => {
        setReportDetail(r.data);
        setReportPage(1);
        setReport(null);
        setSelectedOutlet(null);
        // Pre-fill filter controls so the visible state matches the loaded report
        setSelectedCategory(r.data.report.category);
        setSelectedSubCategory(r.data.report.sub_category || '');
        setSelectedCity(r.data.report.city || '');
      })
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoadingReport(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ─── Actions ─── */

  const handleGenerate = async () => {
    if (!selectedClient || !selectedCategory) return;
    setGenerating(true);
    setReport(null);
    setReportDetail(null);
    setSelectedOutlet(null);
    setReportPage(1);

    try {
      const res = await api.post<ReportSummary>('/api/gaps/generate', {
        client_name: selectedClient,
        country,
        city: selectedCity || null,
        outlet_type: selectedType || null,
        category: selectedCategory,
        sub_category: selectedSubCategory || null,
        currency,
        market_share_pct: parseFloat(marketShare) || 20,
        client_avg_revenue: clientAvgRevenue ? parseFloat(clientAvgRevenue) : null,
      });
      setReport(res.data);
      loadSavedReports();
      toast.success('Report generated', `${res.data.total_gap_outlets.toLocaleString()} gap outlets found`);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const loadReport = async (reportId: string, pg: number = 1) => {
    setLoadingReport(true);
    setSelectedOutlet(null);
    try {
      const res = await api.get<ReportDetail>(`/api/gaps/reports/${reportId}`, {
        params: { page: pg, limit: 25 },
      });
      setReportDetail(res.data);
      setReportPage(pg);
      setReport(null);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  const deleteReport = async (reportId: string) => {
    try {
      await api.delete(`/api/gaps/reports/${reportId}`);
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
      if (reportDetail?.report.id === reportId) setReportDetail(null);
      toast.success('Report deleted');
    } catch {
      toast.error('Failed to delete report');
    }
  };

  const handleExport = async (reportId: string) => {
    setExportLoading(true);
    try {
      const res = await api.get(`/api/gaps/reports/${reportId}/export`, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = 'gap_analysis.xlsx';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";\n]+)"?/);
        if (m) link.download = m[1];
      }
      link.click();
      URL.revokeObjectURL(href);
      toast.success('Download started');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  /* ─── Derived values ─── */

  const currentOutlets = report?.outlets ?? reportDetail?.outlets ?? [];
  const currentTotal = report?.total_gap_outlets ?? reportDetail?.total ?? 0;
  const currentValue = report?.total_opportunity_value ?? reportDetail?.report.total_opportunity_value ?? 0;
  const currentCurrency = report?.currency ?? reportDetail?.report.currency ?? currency;
  const currentReportId = report?.report_id ?? reportDetail?.report.id;
  const currentPages = reportDetail?.pages ?? (report ? 1 : 0);
  const avgPerOutlet = currentTotal > 0 ? currentValue / currentTotal : 0;
  const currentSharePct = reportDetail?.report.market_share_pct ?? (parseFloat(marketShare) || 20);
  const hasReport = currentOutlets.length > 0 || currentTotal > 0;
  const canGenerate = !!selectedClient && !!selectedCategory;

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary, margin: 0,
          letterSpacing: '-0.02em',
        }}>Distribution gaps</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Identify distribution gaps with volume potential estimates</div>
      </div>

      {/* Filter Panel */}
      <Card>
        {/* Row 1 */}
        <div style={filterRowStyle}>
          <FilterField label="Client">
            {viewAsClient ? (
              <div style={{
                padding: '9px 14px',
                background: theme.surface.inset,
                border: `1px solid ${theme.borderColor.subtle}`,
                borderRadius: 8,
                color: theme.brand.cyanLight,
                fontWeight: 600,
                fontSize: 13,
              }}>{maskClientName(viewAsClient)}</div>
            ) : (
              <Select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                <option value="">Select a client</option>
                {clients.map((c, i) => <option key={c} value={c}>{maskClientName(c, i)}</option>)}
              </Select>
            )}
          </FilterField>

          <FilterField label="City">
            <Select value={selectedCity} onChange={e => setSelectedCity(e.target.value)}>
              <option value="">All cities</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FilterField>

          <FilterField label="Outlet type">
            <Select value={selectedType} onChange={e => setSelectedType(e.target.value)}>
              <option value="">All types</option>
              {OUTLET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FilterField>
        </div>

        {/* Row 2 */}
        <div style={{ ...filterRowStyle, marginTop: 14 }}>
          <FilterField label="Category">
            <Select
              value={selectedCategory}
              onChange={e => { setSelectedCategory(e.target.value); setSelectedSubCategory(''); }}
            >
              <option value="">Select category…</option>
              {Object.keys(categories).map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </FilterField>

          <FilterField label="Sub-category">
            <Select
              value={selectedSubCategory}
              onChange={e => setSelectedSubCategory(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value="">All sub-categories</option>
              {(categories[selectedCategory] || []).map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FilterField>

          <FilterField label="Currency">
            <Select value={currency} onChange={e => setCurrency(e.target.value)}>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="RSD">RSD</option>
            </Select>
          </FilterField>
        </div>

        {/* Row 3 — Market share + advanced toggle */}
        <div style={{
          display: 'flex', gap: 14, alignItems: 'flex-end',
          marginTop: 14, flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 160 }}>
            <FilterField label="Market share %">
              <Input
                type="number"
                value={marketShare}
                onChange={e => setMarketShare(e.target.value)}
                min={1} max={100} step={1}
              />
            </FilterField>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            rightIcon={showAdvanced ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />}
          >{showAdvanced ? 'Hide advanced' : 'Advanced options'}</Button>
        </div>

        {showAdvanced && (
          <div style={{ ...filterRowStyle, marginTop: 14 }}>
            <FilterField
              label={`Your avg revenue per outlet (${currency})`}
              hint="Overrides market baseline with your own data"
            >
              <Input
                type="number"
                value={clientAvgRevenue}
                onChange={e => setClientAvgRevenue(e.target.value)}
                placeholder="Leave blank for baseline"
              />
            </FilterField>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Button
            variant="primary"
            fullWidth
            size="lg"
            loading={generating}
            disabled={!canGenerate || generating}
            onClick={handleGenerate}
            leftIcon={<Icon.Chart size={16} />}
          >
            {generating ? 'Generating report…' : 'Generate report'}
          </Button>
        </div>
      </Card>

      {/* Empty state */}
      {!hasReport && !generating && (
        <Card style={{ marginTop: 32 }}>
          <EmptyState
            icon={<Icon.Chart size={28} />}
            title="Configure your filters to generate a gap analysis report"
            description="Select a client and FMCG category, then click Generate Report to identify distribution gaps with revenue potential estimates."
          />
        </Card>
      )}

      {/* Current Report */}
      {hasReport && !generating && (
        <>
          <div style={{ marginTop: 28 }}>
            <StatGrid columns={4}>
              <StatCard
                label="Total gap outlets"
                value={currentTotal.toLocaleString()}
              />
              <StatCard
                primary
                label="Total opportunity"
                value={fmtCurrency(currentValue, currentCurrency)}
              />
              <StatCard
                label="Avg per outlet"
                value={fmtCurrency(avgPerOutlet, currentCurrency)}
              />
              <StatCard
                label="Market share"
                value={`${currentSharePct}%`}
              />
            </StatGrid>
          </div>

          {/* Actions */}
          {currentReportId && (
            <div style={{
              display: 'flex', justifyContent: 'flex-end',
              marginTop: 16, gap: 8,
            }}>
              <Button
                variant="secondary"
                size="sm"
                loading={exportLoading}
                onClick={() => handleExport(currentReportId)}
                leftIcon={<Icon.Download size={12} />}
              >Export Excel</Button>
            </div>
          )}

          {/* Outlet table */}
          {currentOutlets.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Card padding="none">
                <Table
                  columns={outletColumns(currentCurrency)}
                  data={currentOutlets}
                  rowKey={r => r.census_id}
                  onRowClick={(row) => setSelectedOutlet(selectedOutlet?.census_id === row.census_id ? null : row)}
                />
              </Card>
            </div>
          )}

          {/* Pagination */}
          {reportDetail && currentPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              marginTop: 20, gap: 12,
            }}>
              <Pagination
                page={reportPage}
                totalPages={currentPages}
                onChange={p => loadReport(reportDetail.report.id, p)}
              />
              <span style={{
                fontSize: 12,
                color: theme.text.tertiary,
                fontFamily: theme.font.data,
              }}>
                {currentTotal.toLocaleString()} gap outlets
              </span>
            </div>
          )}

          {currentTotal === 0 && (
            <Card
              style={{
                marginTop: 20,
                maxWidth: 520,
                borderColor: theme.semantic.successBorder,
                background: theme.semantic.successSoft,
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: theme.surface.panel,
                  border: `1px solid ${theme.semantic.successBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: theme.semantic.success,
                  flexShrink: 0,
                }}>
                  <Icon.Check size={16} />
                </div>
                <div>
                  <div style={{
                    color: theme.semantic.success,
                    fontWeight: 600, fontSize: 15,
                    marginBottom: 4,
                  }}>Full coverage in this scope</div>
                  <div style={{
                    color: theme.text.secondary,
                    fontSize: 13, lineHeight: 1.5,
                  }}>No distribution gaps found for the selected filters.</div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Outlet drilldown */}
      {selectedOutlet && (
        <div style={{ marginTop: 20 }}>
          <Card
            title="Outlet detail"
            action={
              <IconButton
                icon={<Icon.X size={14} />}
                size="sm"
                onClick={() => setSelectedOutlet(null)}
                aria-label="Close detail"
              />
            }
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
              fontSize: 13,
            }}>
              <DetailField label="Census ID">
                <span
                  onClick={() => navigate(`/outlet/${selectedOutlet.census_id}`)}
                  style={{
                    color: theme.brand.cyanLight,
                    cursor: 'pointer',
                    fontFamily: theme.font.data,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                >{selectedOutlet.census_id}</span>
              </DetailField>
              <DetailField label="Outlet name" value={selectedOutlet.outlet_name || '—'} />
              <DetailField label="Type" value={selectedOutlet.outlet_type || '—'} />
              <DetailField label="City" value={selectedOutlet.city || '—'} />
              <DetailField label="Address" value={selectedOutlet.address || '—'} />
              <DetailField label="Estimated annual value">
                <span style={{
                  color: theme.brand.cyanLight,
                  fontFamily: theme.font.data,
                  fontWeight: 700,
                  fontSize: 16,
                }}>{fmtFull(selectedOutlet.estimated_annual_value, currentCurrency)}</span>
              </DetailField>
            </div>

            {/* Calc breakdown */}
            {(selectedOutlet.baseline_used || selectedOutlet.location_multiplier) && (
              <div style={{
                marginTop: 18,
                padding: '12px 14px',
                background: theme.surface.inset,
                border: `1px solid ${theme.borderColor.subtle}`,
                borderRadius: 8,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: theme.text.tertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 8,
                }}>How is this calculated?</div>
                <div style={{
                  fontSize: 13,
                  color: theme.text.secondary,
                  lineHeight: 1.6,
                }}>
                  Baseline revenue:{' '}
                  <DataValue>{fmtFull(selectedOutlet.baseline_used || 0, currentCurrency)}</DataValue>
                  {' \u00D7 '}
                  Location multiplier:{' '}
                  <DataValue>{selectedOutlet.location_multiplier?.toFixed(2) || '1.00'}x</DataValue>
                  {' \u00D7 '}
                  Market share:{' '}
                  <DataValue>{currentSharePct}%</DataValue>
                  {' = '}
                  <span style={{
                    color: theme.brand.cyanLight,
                    fontFamily: theme.font.data,
                    fontWeight: 700,
                  }}>{fmtFull(selectedOutlet.estimated_annual_value, currentCurrency)}</span>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Saved Reports */}
      {savedReports.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            rightIcon={showHistory ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />}
          >
            Saved reports ({savedReports.length})
          </Button>

          {showHistory && (
            <div style={{ marginTop: 14 }}>
              <Card padding="none">
                <Table
                  columns={savedReportColumns(maskClientName, loadReport, handleExport, deleteReport, loadingReport)}
                  data={savedReports}
                  rowKey={r => r.id}
                  compact
                />
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ TABLE COLUMNS ═══════════════ */

function outletColumns(currency: string): Column<ReportOutlet>[] {
  return [
    {
      key: 'outlet_name',
      header: 'Outlet name',
      render: r => <span style={{ color: theme.text.primary }}>{r.outlet_name || '—'}</span>,
    },
    {
      key: 'outlet_type',
      header: 'Type',
      render: r => <span style={{ color: theme.text.secondary }}>{r.outlet_type || '—'}</span>,
    },
    {
      key: 'city',
      header: 'City',
      render: r => <span style={{ color: theme.text.secondary }}>{r.city || '—'}</span>,
    },
    {
      key: 'address',
      header: 'Address',
      render: r => (
        <span style={{
          color: theme.text.tertiary,
          fontSize: 12,
        }}>{r.address || '—'}</span>
      ),
    },
    {
      key: 'estimated_annual_value',
      header: 'Est. annual value',
      align: 'right',
      render: r => (
        <span style={{
          color: theme.brand.cyanLight,
          fontFamily: theme.font.data,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}>{fmtFull(r.estimated_annual_value, currency)}</span>
      ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      width: 110,
      render: r => <Tag kind={confTagKind(r.confidence)} size="sm">{r.confidence}</Tag>,
    },
  ];
}

function savedReportColumns(
  maskClientName: (n: string) => string,
  loadReport: (id: string) => void,
  handleExport: (id: string) => void,
  deleteReport: (id: string) => void,
  loadingReport: boolean,
): Column<SavedReport>[] {
  return [
    {
      key: 'created_at',
      header: 'Date',
      mono: true,
      width: 100,
      render: r => (
        <span style={{
          color: theme.text.secondary,
          fontSize: 12,
          whiteSpace: 'nowrap',
        }}>
          {r.created_at
            ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
            : '—'}
        </span>
      ),
    },
    {
      key: 'client_name',
      header: 'Client',
      render: r => <span style={{ color: theme.text.primary }}>{maskClientName(r.client_name)}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      render: r => (
        <span style={{ color: theme.text.secondary }}>
          {r.category}
          {r.sub_category && r.sub_category !== 'DEFAULT' ? ` / ${r.sub_category}` : ''}
        </span>
      ),
    },
    {
      key: 'city',
      header: 'City',
      render: r => <span style={{ color: theme.text.secondary }}>{r.city || 'All'}</span>,
    },
    {
      key: 'total_gap_outlets',
      header: 'Outlets',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: theme.text.primary,
          fontVariantNumeric: 'tabular-nums',
        }}>{r.total_gap_outlets.toLocaleString()}</span>
      ),
    },
    {
      key: 'total_opportunity_value',
      header: 'Opportunity',
      mono: true,
      align: 'right',
      render: r => (
        <span style={{
          color: theme.brand.cyanLight,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}>{fmtCurrency(r.total_opportunity_value, r.currency)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 200,
      render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={loadingReport}
            onClick={(e) => { e.stopPropagation(); loadReport(r.id); }}
          >View</Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleExport(r.id); }}
          >Export</Button>
          <IconButton
            icon={<Icon.Trash size={12} />}
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
            aria-label="Delete report"
          />
        </div>
      ),
    },
  ];
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

function FilterField({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 11, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 6,
      }}>{label}</label>
      {children}
      {hint && (
        <div style={{
          fontSize: 11,
          color: theme.text.tertiary,
          marginTop: 4,
          fontStyle: 'italic',
        }}>{hint}</div>
      )}
    </div>
  );
}

function DetailField({
  label, value, children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 600,
        color: theme.text.tertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        marginBottom: 4,
      }}>{label}</div>
      {children || <div style={{ color: theme.text.primary, fontSize: 13 }}>{value}</div>}
    </div>
  );
}

function DataValue({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      color: theme.text.primary,
      fontFamily: theme.font.data,
      fontWeight: 500,
    }}>{children}</span>
  );
}

const filterRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 14,
};
